import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { MapPin, ArrowRight } from "lucide-react";
import { API } from "../lib/api";
import { useUserLocation } from "../lib/location";
import { INDIA } from "../data/india";
import CarCard from "../components/CarCard";
import NearbyCars from "../components/NearbyCars";
import DealerCard from "../components/DealerCard";
import BannerSlider from "../components/BannerSlider";

const BG = "https://images.unsplash.com/photo-1777175013302-eaf4b3ef785a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=2000";

export default function CityPage() {
  const { state, city } = useParams();
  const navigate = useNavigate();
  const { location } = useUserLocation();
  const [cars, setCars] = useState({ items: [], total: 0 });
  const [owners, setOwners] = useState(0);
  const [dealers, setDealers] = useState([]);
  const locKey = location?.city ? `${location.state}|${location.city}` : "";
  const prevLoc = useRef(locKey);

  useEffect(() => {
    // Picking a new city in the top bar while here opens that city's page.
    if (prevLoc.current !== locKey && location?.city && location.city !== city) navigate(`/city/${encodeURIComponent(location.state)}/${encodeURIComponent(location.city)}`);
    prevLoc.current = locKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locKey]);

  useEffect(() => {
    axios.get(`${API}/listings`, { params: { state, city, limit: 12 } }).then((r) => setCars(r.data));
    axios.get(`${API}/listings`, { params: { state, city, seller_type: "private", limit: 1 } }).then((r) => setOwners(r.data.total));
    axios.get(`${API}/dealers`, { params: { state, city } }).then((r) => setDealers(r.data));
    document.title = `Used Cars in ${city}, ${state} | CarSaleMarket.com`;
  }, [state, city]);

  const nearby = (INDIA[state] || []).filter((c) => c !== city).slice(0, 14);
  return (
    <div data-testid="city-page">
      <section className="relative pt-[100px] lg:pt-28 pb-8 grain overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#08090C] via-[#08090C]/70 to-[#08090C]/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <BannerSlider state={state} city={city} className="mb-6" />
          <Link to="/cities" className="eyebrow flex items-center gap-2 mb-4 hover:text-[#F5E08F]" data-testid="city-breadcrumb"><MapPin className="w-3.5 h-3.5" />{state}</Link>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl rise" data-testid="city-title">Used cars in <span className="gold-text italic">{city}</span></h1>
          <div className="flex flex-wrap gap-10 mt-10">
            {[["Cars for sale", cars.total], ["Owner listings", owners], ["Dealer stores", dealers.length]].map(([l, v]) => (
              <div key={l} className="border-l gold-line pl-4"><p className="font-display text-4xl gold-text">{v}</p><p className="text-xs text-gray-400 uppercase tracking-widest">{l}</p></div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-10">
            <Link to={`/cars?state=${encodeURIComponent(state)}&city=${encodeURIComponent(city)}&seller_type=private`} className="btn-outline text-sm" data-testid="city-owner-cars">Owner cars</Link>
            <Link to={`/cars?state=${encodeURIComponent(state)}&city=${encodeURIComponent(city)}&seller_type=dealer`} className="btn-outline text-sm" data-testid="city-dealer-cars">Dealer cars</Link>
            <Link to="/sell" className="btn-gold text-sm" data-testid="city-sell">Sell your car in {city}</Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-8 gap-4">
          <h2 className="font-display text-4xl">Cars in {city}</h2>
          {cars.total > 12 && <Link to={`/cars?state=${encodeURIComponent(state)}&city=${encodeURIComponent(city)}`} className="btn-outline text-sm">View all <ArrowRight className="w-4 h-4" /></Link>}
        </div>
        {!cars.items.length ? (
          <div className="lux-card p-12 text-center text-gray-400" data-testid="city-empty">No cars listed in {city} yet. Be the first — <Link to="/sell" className="text-[#E5C158]">list your car free</Link>.</div>
        ) : <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">{cars.items.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}</div>}
      </section>

      <NearbyCars state={state} city={city} cityCount={cars.total} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" />

      {dealers.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-display text-4xl mb-8">Dealer stores in {city}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">{dealers.map((d) => <DealerCard key={d.id} dealer={d} />)}</div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="eyebrow mb-4">Other cities in {state}</p>
        <div className="flex flex-wrap gap-2.5">
          {nearby.map((c) => <Link key={c} to={`/city/${encodeURIComponent(state)}/${encodeURIComponent(c)}`} className="px-4 py-2 rounded-full border border-white/10 text-sm text-gray-300 hover:border-[#D4AF37] hover:text-[#F5E08F] transition-colors">{c}</Link>)}
        </div>
      </section>
    </div>
  );
}
